import { useTranslation } from 'react-i18next'
import { useYupFields } from 'hooks/forms/useYupFields'
import { DynamicQuestionCode } from 'types/dynamicFields'
import { DynamicQuestionFieldTypes } from './constants'
import { DynamicQuestionFieldType } from './types'
import { getIsAdditionalFieldRequired } from './utils'

export const useDynamicQuestionSchema = () => {
  const { t } = useTranslation()
  const requiredErrorMessage = t('fieldRequiredGeneral')

  const {
    getYupField,
    getRequiredYupField,
    getEmailYupField,
    getRequiredRadioYupField,
    getConditionalYupField,
  } = useYupFields()

  const getOptionalFieldSchema = (type: DynamicQuestionFieldType, label: string) => {
    const optionalSchema = getYupField({ label: `${label} ${t('optional')}` })
    if (type === DynamicQuestionFieldTypes.RADIO) {
      return optionalSchema?.nullable()
    }
    return optionalSchema
  }

  const getFieldSchema = (
    key: DynamicQuestionCode,
    type: DynamicQuestionFieldType,
    isOptional?: boolean
  ) => {
    const label = t(`businessCompliance.questionLabel.${key}`)
    if (isOptional) {
      return getOptionalFieldSchema(type, label)
    }
    switch (type) {
      case DynamicQuestionFieldTypes.RADIO:
        return getRequiredRadioYupField(label, t('emptySelect'))
      case DynamicQuestionFieldTypes.EMAIL:
        return getEmailYupField(label, requiredErrorMessage)
      default:
        return getRequiredYupField({ label, requiredErrorMessage })
    }
  }

  const getContactLastNameFieldSchema = () => {
    return getRequiredYupField({ label: t('name'), requiredErrorMessage })
  }

  const getContactEmailFieldSchema = () => {
    return getEmailYupField(t('workEmail'), requiredErrorMessage)
  }

  const getAdditionalFieldSchema = (key: DynamicQuestionCode) => {
    return getConditionalYupField<string>({
      fieldKey: key,
      fieldValue: value => getIsAdditionalFieldRequired(key, value),
      label: t(`businessCompliance.additionalLabel.${key}`),
      requiredErrorMessage,
    })
  }

  return {
    getFieldSchema,
    getAdditionalFieldSchema,
    getContactLastNameFieldSchema,
    getContactEmailFieldSchema,
  }
}
